import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { z } from "zod";
import {
  APP_RAIL,
  DETAIL_OVERHEAD,
  type FittedPanes,
  fitPaneLayout,
  type PaneLayout,
  PHONE_MAX_WIDTH,
  parsePaneLayout,
} from "../../src/pane-layout.ts";
import { detachedSlugPath, type SettingsSection } from "../../src/settings-sections.ts";
import {
  type ChordState,
  chordFor,
  type GoTo,
  isTyping,
  type Shortcut,
  shortcutFor,
} from "../../src/shortcuts.ts";
import { landingWorkspace } from "../../src/workspace-status.ts";
import { assignmentInUseRemembered, useAssignment } from "../inbox-v2/assignment.tsx";
import { ViewRow } from "../inbox-v2/saved-views.tsx";
import { forgetUnsent } from "../inbox-v2/unsent-reply.ts";
import {
  useInboxAccess,
  useInboxCounts,
  useInboxViews,
  useNeedsReplyBadges,
} from "../inbox-v2/use-inbox.ts";
import { LeaveConfirm } from "../leave-confirm.tsx";
import { ImagePreferenceMenuItem, ImagePreferenceTrayRow } from "../mail/image-preference.tsx";
import { PageShortcutsContext, type ShortcutEntry } from "../page-shortcuts.tsx";
import { PhoneMenu } from "../phone-menu.tsx";
import { readPreference } from "../preferences.ts";
import {
  type SessionSchema,
  sessionQuery,
  type WorkspacesSchema,
  workspacesQuery,
} from "../queries.ts";
import { InboxPanel, Rail } from "../rail.tsx";
import { useCanAddWorkspaces } from "../settings/workspaces.tsx";
import {
  type FolderChoice,
  folderChoiceFrom,
  INBOX_SECTION,
  type MailChoice,
  MINE_OR_UNASSIGNED,
  mailChoiceFrom,
  mailHref,
  type NavRow,
  type Owner,
  ownerFrom,
  type ShellNav,
  type ShellPage,
  shellNav,
} from "../shell-nav.ts";
import {
  campaignsPath,
  inboxPath,
  NavigateContext,
  settingsPath,
  WorkspaceContext,
} from "../workspace-context.tsx";
import { type ReadState, readState, type SessionState, sessionState } from "./read-state.ts";
import { ShortcutsPanel } from "./shell-parts.tsx";
import { SignIn } from "./sign-in.tsx";
import {
  FAILED_PAGE,
  placeOf,
  type ShellGateKind,
  TabTitleContext,
  tabTitle,
  useTabTitle,
} from "./tab-title.ts";
import { DarkThemeContext, useTheme } from "./theme.ts";

/*
 * The app shell: the rail, the phone bar and its menu, the theme, the session, the workspace list,
 * sign-out and the keyboard-shortcuts panel, around whichever page is on screen. It was the outer
 * half of `Workspace` in `main.tsx` until the master-inbox plan's step 1
 * (docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md), lifted out verbatim so another page can
 * render inside the same frame. The page is a child that receives the shell's state; the shell owns
 * nothing a page draws.
 */

/** The shell's one request function: the page module's own, so every request reads failures alike. */
type ShellRequest = (path: string, body?: object) => Promise<unknown>;

type ShellWorkspace = z.infer<typeof WorkspacesSchema>["workspaces"][number];
const PlanWorkspaceSchema = z.object({ workspaceId: z.string() });
type ShellUser = NonNullable<z.infer<typeof SessionSchema>>["user"];
/** The workspace this browser last showed, so `/` returns to it. A preference, never authority. */
const LAST_WORKSPACE = "leadcue-reply-workspace";
/**
 * Remember a workspace for the next page that names none: an old settings address under a workspace
 * redirects to an agency or account address (LEA-501), which then shows the client the link named.
 */
export function rememberWorkspace(id: string): void {
  try {
    localStorage.setItem(LAST_WORKSPACE, id);
  } catch {
    // Storage unavailable: the detached page falls back to the first workspace, which is not an error.
  }
}
function rememberedWorkspace(): string | null {
  try {
    return readPreference(localStorage, LAST_WORKSPACE);
  } catch {
    return null;
  }
}

/** Each workspace with its needs-reply count where its badge read answered one. */
function withBadges(
  workspaces: readonly ShellWorkspace[] | undefined,
  badges: ReadonlyMap<string, number>,
): readonly ShellWorkspace[] {
  if (!workspaces) return [];
  return workspaces.map((workspace) => {
    // Only the new badge read has work-count semantics; discard the legacy list's unseen count.
    const needsReply = badges.get(workspace.id);
    const { unseen: _listed, ...rest } = workspace;
    return needsReply === undefined ? rest : { ...rest, needsReply };
  });
}

/** What a page inside the shell reads from it. */
export interface Shell {
  readonly current: ShellWorkspace;
  /** The other workspaces this person may open, as the workspace list sends them. */
  readonly others: readonly ShellWorkspace[];
  readonly user: ShellUser;
  /** The rows of the rail and the inbox's panel, which a folded panel lends the list's tabs. */
  readonly nav: ShellNav | null;
  /** Where in the master inbox the address points; home on any other page. */
  readonly mailChoice: MailChoice;
  /** Whose threads the address narrows to (the rail's scope); null is everyone. */
  readonly owner: Owner;
  /** The More folder the address opens instead of the inbox's list; null outside one. */
  readonly folder: FolderChoice | null;
  readonly dark: boolean;
  readonly toggleTheme: () => void;
  readonly signOut: () => void;
  /** Follows a link in place, asking first when the page holds an unsaved edit. */
  readonly leave: (href: string) => void;
  /** Runs `move` now, or once the rep chooses to discard an unsaved edit. */
  readonly guarded: (move: () => void) => void;
  readonly setUnsaved: (dirty: boolean) => void;
  readonly layout: PaneLayout;
  readonly fitted: FittedPanes;
  readonly changeLayout: (next: PaneLayout, done: boolean) => void;
  readonly onPhone: boolean;
  readonly shortcutsOpen: boolean;
  readonly setShortcutsOpen: (open: boolean) => void;
}

/*
 * Column widths, the rail's included, are a per-browser preference; storage can be unavailable,
 * which is a default and not an error, as with the theme.
 */
function useShellLayout() {
  const [layout, setLayout] = useState<PaneLayout>(() => {
    try {
      return parsePaneLayout(readPreference(localStorage, "leadcue-reply-pane-layout"));
    } catch {
      return parsePaneLayout(null);
    }
  });
  const [viewport, setViewport] = useState(window.innerWidth);
  useEffect(() => {
    const measure = () => setViewport(window.innerWidth);
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const changeLayout = (next: PaneLayout, done: boolean): void => {
    setLayout(next);
    if (!done) return;
    try {
      localStorage.setItem("leadcue-reply-pane-layout", JSON.stringify(next));
    } catch {
      // Private mode or blocked storage: the layout still applies for this visit.
    }
  };
  // Below the phone breakpoint neither the rail nor the panel is drawn: the top bar takes over.
  const onPhone = viewport <= PHONE_MAX_WIDTH;
  /*
   * The panes fitted to the window (`src/pane-layout.ts`): the rail, the inbox's panel where the
   * page has one, the list and the thread. The panel narrows, then the list, then the panel folds.
   */
  const fit = (panel: boolean): FittedPanes =>
    fitPaneLayout(layout, viewport, DETAIL_OVERHEAD, { phone: onPhone, panel });
  return { layout, viewport, changeLayout, onPhone, fit };
}

/*
 * One workspace at a time (docs/2026-09-19-workspace-scope-critique.md). `/` is not a view: it
 * resolves to the plan's own workspace for an old `/?plan=` link, else the last one this browser
 * showed, else the first by name. The workspace on screen is remembered for the next `/`.
 */
function useResolvedWorkspace(
  workspaceId: string | null,
  list: readonly ShellWorkspace[] | undefined,
  current: ShellWorkspace | null,
  request: ShellRequest,
  resolve = true,
): boolean {
  const navigate = useNavigate();
  const [unresolvable, setUnresolvable] = useState(false);
  useEffect(() => {
    if (!resolve || workspaceId !== null || !list) return;
    // The remembered workspace while it is listed; an offboarded one is not.
    const fallback = landingWorkspace(list, rememberedWorkspace());
    const plan = new URLSearchParams(location.search).get("plan");
    let cancelled = false;
    void (async () => {
      const owner = plan
        ? await request(`/api/work/plans/${encodeURIComponent(plan)}/workspace`)
            .then((body) => PlanWorkspaceSchema.parse(body).workspaceId)
            .catch(() => null)
        : null;
      if (cancelled) return;
      if (owner) {
        await navigate({ href: `${inboxPath(owner)}${location.search}`, replace: true });
        return;
      }
      if (!fallback) {
        setUnresolvable(true);
        return;
      }
      await navigate({ href: inboxPath(fallback.id), replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, list, navigate, request, resolve]);
  useEffect(() => {
    if (!current) return;
    try {
      localStorage.setItem(LAST_WORKSPACE, current.id);
    } catch {
      // Storage unavailable: `/` falls back to the first workspace, which is not an error.
    }
  }, [current]);
  return unresolvable;
}

/*
 * Keyboard model (src/shortcuts.ts). One listener for every page: Escape closes the shortcuts panel
 * wherever you are, and the rest goes to the page on screen. Keys inside an open menu belong to the
 * menu. The handler is rebuilt each render so it sees the current state. `capture` listens before
 * any page does: the go-to chords and `W` (LEA-501), whose second key a page's own M, C or A must
 * never also act on, and the campaign preview listens on the document as the shell does.
 */
function useShellKeys(take: (event: KeyboardEvent) => void, capture = false): void {
  const handler = useRef(take);
  handler.current = take;
  useEffect(() => {
    const listen = (event: KeyboardEvent) => handler.current(event);
    document.addEventListener("keydown", listen, capture);
    return () => document.removeEventListener("keydown", listen, capture);
  }, [capture]);
}

/** Where a go-to chord leads, among the rows this person may reach; null where it is not one. */
function goToHref(nav: ShellNav | null, place: GoTo): string | null {
  if (!nav) return null;
  const mail = (key: string) => nav.mail.find((row) => row.key === key)?.href ?? null;
  const section = (key: string) => nav.sections.find((row) => row.key === key)?.href ?? null;
  switch (place) {
    case "inbox":
      return section(INBOX_SECTION) ?? nav.home;
    case "meeting":
      return mail("meeting");
    case "needs_reply":
      return mail("needs_reply");
    case "follow_up":
      return mail("follow_up");
    case "campaigns":
      return section("campaigns");
    case "answers":
      return section("answers");
    case "settings":
      return nav.settings?.href ?? null;
  }
}

/** The phone's top bar: the brand, which goes home, and the menu sheet. */
function TopBar(props: { signedIn: boolean; dark: boolean; onHome: () => void; menu: ReactNode }) {
  return (
    <header className="lcr-top" data-menu={props.signedIn ? "sheet" : undefined}>
      <a
        className="lcr-product-brand"
        href="/"
        aria-label="LeadCue inbox"
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          props.onHome();
        }}
      >
        <img
          className="lcr-wordmark"
          src={props.dark ? "/leadcue-logo-white.svg" : "/leadcue-logo-dark.svg"}
          alt="LeadCue"
        />
      </a>
      <div className="lcr-top-right">{props.menu}</div>
    </header>
  );
}

/*
 * What stands in the page's place until there is a page: the session loading, sign-in, a workspace
 * list that failed or is still resolving, and a workspace this person cannot see.
 */
/** A read that failed with nothing to show: what failed, its reference, and Try again. */
export function LoadFailedPage(props: {
  what: string;
  reference?: string | null;
  onRetry: () => void;
}) {
  useTabTitle(FAILED_PAGE);
  return (
    <LoadFailed what={props.what} reference={props.reference ?? null} onRetry={props.onRetry} />
  );
}
function LoadFailed(props: { what: string; reference: string | null; onRetry: () => void }) {
  return (
    <div className="lcr-empty-detail lcr-unavailable" role="alert">
      <h2>Could not load {props.what}</h2>
      <p>
        Nothing was changed. Try again in a moment
        {props.reference ? `; if it keeps failing, quote reference ${props.reference}.` : "."}
      </p>
      <button type="button" className="lcr-small-button" onClick={props.onRetry}>
        Try again
      </button>
    </div>
  );
}

/**
 * A refresh that failed above a page still showing what it last loaded: said, and offered again.
 * The page underneath keeps its rows, its selection and any unsent text (mitigation plan, F04).
 */
function StaleNotice(props: { what: string; reference: string | null; onRetry: () => void }) {
  return (
    <div className="lcr-stale-notice" role="status">
      <span>
        Could not refresh {props.what}. Showing what was last loaded
        {props.reference ? ` (reference ${props.reference})` : ""}.
      </span>
      <button type="button" className="lcr-linklike" onClick={props.onRetry}>
        Try again
      </button>
    </div>
  );
}

/*
 * What stands in the page's place until there is a page: the session loading or unreachable,
 * sign-in, a workspace list that failed or is still resolving, and a workspace this person cannot
 * see. A list that only failed to refresh keeps the page, with a notice above it.
 */
type GateInput = {
  session: SessionState;
  workspaces: ReadState;
  list: readonly ShellWorkspace[] | undefined;
  waiting: boolean;
  current: ShellWorkspace | null;
  personal?: boolean;
  /**
   * A settings page outside any workspace (LEA-501): it waits for the workspace list, to name the
   * remembered client in the rail, but renders without one, so an agency with none can add one.
   */
  detached?: boolean;
};

/** Which of those the shell draws: the gate below draws it, and the tab's title names it. */
function gateOf({
  session,
  workspaces,
  list,
  waiting,
  current,
  personal,
  detached,
}: GateInput): ShellGateKind {
  if (session.kind === "loading") return "loading";
  if (session.kind === "unreachable") return "session_failed";
  if (session.kind === "signed_out" || workspaces.kind === "signed_out") return "sign_in";
  if (personal) return "page";
  if (detached) return workspaces.kind === "loading" ? "loading" : "page";
  if (workspaces.kind === "refused") return "refused";
  if (workspaces.kind === "failed") return "workspaces_failed";
  if (workspaces.kind === "loading" || !list || waiting) return "loading";
  return current ? "page" : "unavailable";
}

function ShellGate(
  props: GateInput & {
    onSignedIn: () => Promise<void>;
    onRetrySession: () => void;
    onRetryWorkspaces: () => void;
    /** This page saw a session that has since ended, so the sign-in says so. */
    ended: boolean;
    page: () => ReactNode;
  },
) {
  const { session, workspaces } = props;
  const gate = gateOf(props);
  if (gate === "loading") return <p className="lcr-loading">Loading…</p>;
  if (gate === "session_failed")
    return (
      <LoadFailed
        what="your session"
        reference={session.kind === "unreachable" ? session.reference : null}
        onRetry={props.onRetrySession}
      />
    );
  if (gate === "sign_in") return <SignIn onSuccess={props.onSignedIn} ended={props.ended} />;
  if (gate === "refused")
    return (
      <div className="lcr-empty-detail lcr-unavailable" role="alert">
        <h2>You no longer have access here</h2>
        <p>Ask an admin of your organization if you think you should.</p>
      </div>
    );
  if (gate === "workspaces_failed")
    return (
      <LoadFailed
        what="your workspaces"
        reference={workspaces.kind === "failed" ? workspaces.reference : null}
        onRetry={props.onRetryWorkspaces}
      />
    );
  if (gate === "page")
    return (
      <>
        {workspaces.kind === "ready" && workspaces.stale && (
          <StaleNotice
            what="your workspaces"
            reference={workspaces.stale.reference}
            onRetry={props.onRetryWorkspaces}
          />
        )}
        {props.page()}
      </>
    );
  const list = props.list ?? [];
  return (
    <div className="lcr-empty-detail lcr-unavailable">
      <h2>This workspace is not available</h2>
      <p>
        {list.length
          ? "It may have been removed, or it belongs to another organization."
          : "Your organization has no workspace yet."}
      </p>
      {list[0] ? (
        <a className="lcr-small-button" href={inboxPath(list[0].id)}>
          Open {list[0].name}
        </a>
      ) : (
        // Settings outside a workspace: where an agency admin adds the first one (LEA-501).
        <a className="lcr-small-button" href="/settings">
          Open settings
        </a>
      )}
    </div>
  );
}

/*
 * Where switching client lands: the same kind of page where the other client has one, and the master
 * inbox from the master inbox where the other client has it too.
 */
function workspaceHome(
  page: ShellPage,
  workspaces: readonly ShellWorkspace[],
): (id: string) => string {
  return (id) => {
    if (page === "settings") return settingsPath(id);
    if (page === "campaigns" || page === "campaign") return campaignsPath(id);
    const inbox = workspaces.find((workspace) => workspace.id === id)?.inbox === true;
    if (page !== "mail" || !inbox) return inboxPath(id);
    // The switcher's number is "for you": it opens on that list where anything is assigned (LEA-501).
    return mailHref(id, undefined, assignmentInUseRemembered(id) ? MINE_OR_UNASSIGNED : null);
  };
}

/*
 * What the bar says when it has no workspace to name. The whole context block used to be gated on
 * `current`, so a pending or failed workspace list removed the client's name and both views at once
 * and explained none of it in the chrome.
 */
function workspaceStatusText(pending: boolean, failed: boolean): string {
  if (pending) return "Loading workspace…";
  return failed ? "Workspace unavailable" : "No workspace";
}

type ShellProps = {
  workspaceId: string | null;
  page: ShellPage;
  /** The settings section on screen, which names the browser tab on the settings page. */
  section: SettingsSection | null;
  request: ShellRequest;
  children: (shell: Shell) => ReactNode;
  /** A personal account task needs a session, independently of workspace availability. */
  personalPage?: ReactNode;
  /**
   * A settings page whose address names no workspace (agency and account settings, LEA-501). The
   * rail shows the remembered workspace; the page is drawn with it, or with none.
   */
  detachedPage?: (shell: DetachedShell) => ReactNode;
};

/** What a settings page outside a workspace reads from the shell: the client may be none. */
interface DetachedShell {
  readonly current: ShellWorkspace | null;
  readonly user: ShellUser;
  readonly dark: boolean;
  readonly toggleTheme: () => void;
  readonly signOut: () => void;
  readonly leave: (href: string) => void;
}

/**
 * The last person each cache showed data for. It outlives the shell, which mounts per page, and
 * belongs to the cache it describes, so a second cache (a test's next mount) starts unknown.
 */
const shownFor = new WeakMap<QueryClient, string | null>();

/**
 * Data the cache holds for one person never reaches another (mitigation plan, W3): when the session
 * turns out to be someone else's, or no one's, everything but the session itself is dropped, with
 * the reply box's kept text for the person who left, as signing out drops both. `undefined` is a
 * lookup still on its way or failing, which says nothing about who is signed in.
 */
function useIdentityGuard(userId: string | null | undefined): void {
  const cache = useQueryClient();
  useEffect(() => {
    if (userId === undefined) return;
    const previous = shownFor.get(cache) ?? null;
    shownFor.set(cache, userId);
    if (previous === null || previous === userId) return;
    forgetUnsent(previous);
    cache.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
  }, [userId, cache]);
}

/** The session and the workspace list, and the workspace the URL names among them. */
function useShellScope(workspaceId: string | null, request: ShellRequest, personal: boolean) {
  const session = useQuery(sessionQuery());
  const workspaces = useQuery({ ...workspacesQuery(), enabled: !!session.data });
  useIdentityGuard(session.data?.user.id ?? (session.data === null ? null : undefined));
  /*
   * The switcher's needs-reply badges are each workspace's own read, so a slow count can neither delay
   * nor fail the workspace list, nor another workspace's badge (mitigation plan, F03). A workspace
   * whose count could not be read has no badge.
   */
  const inboxIds = workspaces.data?.flatMap((workspace) => (workspace.inbox ? [workspace.id] : []));
  const badges = useNeedsReplyBadges(inboxIds ?? [], !!session.data);
  const listed = withBadges(workspaces.data, badges);
  const current = personal
    ? (landingWorkspace(workspaces.data ?? [], rememberedWorkspace()) ?? null)
    : (workspaces.data?.find((workspace) => workspace.id === workspaceId) ?? null);
  const unresolvable = useResolvedWorkspace(
    workspaceId,
    workspaces.data,
    current,
    request,
    !personal,
  );
  // What this person may reach here, and the inbox's totals and saved views where it has one.
  const inbox = current?.inbox === true;
  const access = useInboxAccess(current?.id);
  const counts = useInboxCounts(current?.id ?? "", inbox);
  const views = useInboxViews(current?.id ?? "", inbox);
  const assignment = useAssignment(current?.id ?? "", inbox, counts.data);
  return {
    assignment,
    access: access.data,
    counts: counts.data,
    countsLoading: counts.isLoading,
    /** The counts could not be read and none were before: their slots say unknown, never zero. */
    countsFailed: counts.isError && counts.data === undefined,
    views: views.data,
    session,
    workspaces,
    /** The workspace list with each one's badge, for the switcher. */
    listed,
    current,
    unresolvable,
    user: session.data?.user ?? null,
  };
}

/** The totals the rows count: one owner's while the scope narrows to them, else everyone's. */
function rowCounts(
  owner: Owner,
  scope: ReturnType<typeof useShellScope>,
  scoped: ReturnType<typeof useInboxCounts>,
) {
  if (!owner)
    return {
      counts: scope.counts,
      countsLoading: scope.countsLoading,
      countsFailed: scope.countsFailed,
    };
  return {
    counts: scoped.data,
    countsLoading: scoped.isLoading,
    countsFailed: scoped.isError && scoped.data === undefined,
  };
}

/** The rows of the rail and the phone menu for the workspace on screen; null while there is none. */
function useShellNav(scope: ReturnType<typeof useShellScope>, page: ShellPage) {
  const search = useRouterState({ select: (state) => state.location.searchStr });
  const mailChoice = mailChoiceFrom(page === "mail" ? search : "");
  const folder = page === "mail" ? folderChoiceFrom(search) : null;
  const mailbox = page === "mail" && new URLSearchParams(search).get("mailbox") === "all";
  const owner = page === "mail" ? ownerFrom(search) : null;
  const { current } = scope;
  // The rows count what the scope holds; everyone's totals still say whether anyone assigns.
  const scoped = useInboxCounts(
    current?.id ?? "",
    current?.inbox === true && owner !== null,
    owner,
  );
  const nav: ShellNav | null = current
    ? shellNav({
        workspaceId: current.id,
        inbox: current.inbox === true,
        access: scope.access,
        ...rowCounts(owner, scope, scoped),
        views: scope.views ?? [],
        owner,
        page,
        // A folder is not one of the inbox's places, so no mail row is current in it.
        choice: page === "mail" && !folder && !mailbox ? mailChoice : null,
        mailbox,
        folder,
        assignment: scope.assignment ?? undefined,
      })
    : null;
  return { nav, mailChoice, folder, owner };
}

/** The shell's state: everything the frame draws and everything a page reads from it. */
function useShell({ workspaceId, page, request, detachedPage }: ShellProps) {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const [unsaved, setUnsaved] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  /* Keys another page answers to while it is on screen, such as a campaign's preview. */
  const [pageShortcuts, setPageShortcuts] = useState<readonly ShortcutEntry[] | null>(null);
  const panes = useShellLayout();
  const scope = useShellScope(workspaceId, request, page === "account" || !!detachedPage);
  const { current } = scope;
  const { nav, mailChoice, folder, owner } = useShellNav(scope, page);
  // The inbox's panel is drawn beside the rail on the inbox's pages only (LEA-501). Until the
  // workspace list answers, an inbox page keeps the panel's column, as most workspaces have one.
  const fitted = panes.fit(
    page === "mail" && (current ? current.inbox === true : scope.workspaces.isPending),
  );
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // "Add workspace" is asked for only where it may show: the switcher open, or one workspace.
  const canAddWorkspaces = useCanAddWorkspaces(
    switcherOpen || (scope.workspaces.data?.length ?? 0) <= 1,
  );
  // The rail draws a switcher only where there is somewhere to switch to, or a workspace to add.
  const switchable = current !== null && (scope.listed.length > 1 || canAddWorkspaces);
  const chord = useRef<ChordState>(null);
  /*
   * Leaving with an unsaved edit is asked in the page. The move waits here until the rep answers:
   * "Discard and leave" runs it, "Stay" drops it.
   */
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const guarded = (move: () => void): void => {
    if (unsaved) setPendingLeave(() => move);
    else move();
  };
  const leave = (href: string): void =>
    guarded(() => {
      setUnsaved(false);
      void navigate({ href });
    });
  const goHome = (): void =>
    guarded(() => {
      setUnsaved(false);
      if (page === "inbox" && !current?.inbox) {
        history.replaceState(null, "", current ? inboxPath(current.id) : "/");
      } else if (nav) void navigate({ href: nav.home });
    });
  /*
   * A page's keys are its own (a campaign's preview and the master inbox listen for theirs); the
   * panel that lists them is here. The settings page has no list to move through.
   */
  const forPage = (action: Shortcut, event: KeyboardEvent): void => {
    if (action === "help" && pageShortcuts !== null) {
      setShortcutsOpen(!shortcutsOpen);
      event.preventDefault();
    }
  };
  useShellKeys((event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (event.defaultPrevented || target?.closest('[role="menu"]')) return;
    /*
     * Escape dismisses an open overlay wherever you are. This used to sit below the inbox guard, so
     * on the settings page a panel opened by click had no keyboard exit at all.
     */
    if (event.key === "Escape" && shortcutsOpen) {
      setShortcutsOpen(false);
      event.preventDefault();
      return;
    }
    const action = shortcutFor(event, isTyping(target));
    if (!action) return;
    forPage(action, event);
  });
  /*
   * The navigation belongs to every page, so its keys do too: G then a place, and W for the
   * workspace switcher. Not on a phone, and not inside an open menu or dialog, whose keys are its own.
   */
  useShellKeys((event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (
      panes.onPhone ||
      event.defaultPrevented ||
      target?.closest('[role="menu"], [role="dialog"], [role="listbox"]')
    ) {
      chord.current = null;
      return;
    }
    const step = chordFor(chord.current, event, isTyping(target), performance.now());
    chord.current = step.state;
    // W where there is no switcher is no key of the shell's, so the page may have it.
    if (!step.consumed || (step.action === "workspaces" && !switchable)) return;
    event.preventDefault();
    event.stopPropagation();
    if (step.action === "workspaces") {
      setSwitcherOpen(true);
      return;
    }
    const href = step.action ? goToHref(nav, step.action) : null;
    if (href) leave(href);
  }, true);
  /*
   * The shortcuts panel lists the keys of the page on screen, so it does not survive a move to a page
   * with none. A move by the rail is a click outside it, which closes it anyway.
   */
  const shortcutsAvailable = pageShortcuts !== null;
  useEffect(() => {
    if (!shortcutsAvailable) setShortcutsOpen(false);
  }, [shortcutsAvailable]);
  const signOut = async (): Promise<void> => {
    await request("/api/auth/sign-out", {});
    // Signed out, so what this person typed and kept in the inbox's reply box goes with them.
    if (scope.user) forgetUnsent(scope.user.id);
    cache.clear();
    location.reload();
  };
  const value = (workspace: ShellWorkspace, user: ShellUser): Shell => ({
    current: workspace,
    others: scope.workspaces.data?.filter((other) => other.id !== workspace.id) ?? [],
    user,
    nav,
    mailChoice,
    owner,
    folder,
    dark: theme.dark,
    toggleTheme: theme.toggle,
    signOut: () => void signOut(),
    leave,
    guarded,
    setUnsaved,
    layout: panes.layout,
    fitted,
    changeLayout: panes.changeLayout,
    onPhone: panes.onPhone,
    shortcutsOpen,
    setShortcutsOpen,
  });
  return {
    ...scope,
    nav,
    cache,
    dark: theme.dark,
    toggleTheme: theme.toggle,
    panes,
    fitted,
    switcher: { open: switcherOpen, setOpen: setSwitcherOpen },
    canAddWorkspaces,
    switchable,
    leave,
    goHome,
    signOut,
    shortcuts: { available: shortcutsAvailable, open: shortcutsOpen, setOpen: setShortcutsOpen },
    pageShortcuts,
    setPageShortcuts,
    pendingLeave,
    setPendingLeave,
    value,
  };
}
type ShellState = ReturnType<typeof useShell>;

/*
 * The app rail is the desktop shell; below the phone breakpoint the stylesheet hides it and the top
 * bar and its tray take over. It renders for any session, with or without a resolved workspace, so
 * the chrome never goes quiet about which state it is in. The inbox's panel is drawn beside it on the
 * inbox's pages, where the window seats it (LEA-501).
 */
function ShellRail({ state, page, user }: { state: ShellState; page: ShellPage; user: ShellUser }) {
  /*
   * The keyboard shortcuts document the page's keys, so they belong to the page. The panel opens
   * from the account menu in the rail's foot, or on `?`, and is drawn beside the foot. It used to
   * hang from a rail button with a top-bar dropdown's anchoring and opened off the left edge.
   */
  const shortcutsPanel =
    state.shortcuts.available && state.shortcuts.open ? (
      <ShortcutsPanel
        onClose={() => state.shortcuts.setOpen(false)}
        entries={(state.pageShortcuts ?? []).filter(
          (entry) => state.switchable || entry.keys !== "W",
        )}
      />
    ) : null;
  const inboxHere = state.current?.inbox === true ? state.current.id : null;
  const panelShown = state.fitted.panel > 0 && state.nav !== null && state.nav.mail.length > 0;
  return (
    <>
      <Rail
        workspaces={state.listed}
        current={state.current}
        workspaceStatus={workspaceStatusText(
          state.workspaces.isPending,
          (state.workspaces.isError && state.workspaces.data === undefined) || state.unresolvable,
        )}
        nav={state.nav}
        panelShown={panelShown}
        onNavigate={state.leave}
        onWorkspace={(id) => state.leave(workspaceHome(page, state.workspaces.data ?? [])(id))}
        switcherOpen={state.switcher.open}
        onSwitcherOpen={state.switcher.setOpen}
        addWorkspaceHref={state.canAddWorkspaces ? detachedSlugPath("workspaces") : null}
        user={user}
        dark={state.dark}
        onToggleTheme={state.toggleTheme}
        onSignOut={() => void state.signOut()}
        {...(state.shortcuts.available ? { onShortcuts: () => state.shortcuts.setOpen(true) } : {})}
        footPanel={shortcutsPanel}
        displayOptions={
          state.current && (
            <ImagePreferenceMenuItem
              workspaceId={state.current.id}
              workspaceName={state.current.name}
            />
          )
        }
      />
      {panelShown && state.nav && state.current && (
        <InboxPanel
          nav={state.nav}
          width={state.fitted.panel}
          workspaceName={state.current.name}
          onNavigate={state.leave}
          moreMemory={`lcr-rail-more:${user.id}`}
          {...(inboxHere
            ? {
                viewRow: (row: NavRow, link: ReactNode) => (
                  <ViewRow
                    workspaceId={inboxHere}
                    row={row}
                    link={link}
                    onDeletedCurrent={() => state.leave(mailHref(inboxHere))}
                  />
                ),
              }
            : {})}
        />
      )}
      {!panelShown && state.fitted.panel > 0 && <PanelPending width={state.fitted.panel} />}
    </>
  );
}

/*
 * The rail's column while the session is still being asked. The rail needs the person, and a session
 * answering after the page's first paint inserted it and pushed the whole page right: a layout shift
 * of 0.17 in about one slow-network load in ten in the lab (`tests/browser/vitals-lab.ts`). Signed in,
 * the rail takes the same width; signed out, the sign-in form is drawn without it. On an inbox page
 * the panel's column is kept the same way, until the workspace and its rows are known (LEA-501).
 */
function RailPending({ width }: { width: number }) {
  return <div className="lcr-rail lcr-rail-pending" style={{ width: `${width}px` }} aria-hidden />;
}

function PanelPending({ width }: { width: number }) {
  return (
    <div
      className="lcr-rail lcr-panel lcr-rail-pending"
      style={{ width: `${width}px` }}
      aria-hidden
    />
  );
}

/** The phone bar's menu sheet: the same places, the workspace, the theme and sign-out. */
function ShellPhoneMenu(props: { state: ShellState; page: ShellPage; user: ShellUser }) {
  const { state, page } = props;
  return (
    <PhoneMenu
      user={props.user}
      workspaces={state.listed}
      current={state.current}
      nav={state.nav}
      dark={state.dark}
      onToggleTheme={state.toggleTheme}
      onNavigate={state.leave}
      onWorkspace={(id) => state.leave(workspaceHome(page, state.workspaces.data ?? [])(id))}
      onSignOut={state.signOut}
      displayOptions={
        state.current && (
          <ImagePreferenceTrayRow
            workspaceId={state.current.id}
            workspaceName={state.current.name}
          />
        )
      }
    />
  );
}

/**
 * The keyboard's first stop (LEA-501): past the rail and the panel to the page. A button that moves
 * the focus, not a `#lcr-content` link, which the router would read as an address change.
 */
function SkipLink() {
  return (
    <button
      type="button"
      className="lcr-skip-link"
      onClick={() => {
        const body = document.getElementById("lcr-content");
        const target = body?.querySelector<HTMLElement>("main, [role='main'], aside") ?? body;
        if (target && target !== body && !target.hasAttribute("tabindex"))
          target.setAttribute("tabindex", "-1");
        target?.focus();
      }}
    >
      Skip to content
    </button>
  );
}

/** The frame every signed-in page renders inside; `children` draws the page from the shell's state. */
export function AppShell(props: ShellProps) {
  const state = useShell(props);
  const { user, current, pendingLeave } = state;
  const session = sessionState(state.session);
  const workspaces = readState(state.workspaces);
  // The sign-in draws its own wordmark on the paper (docs/2026-10-19-sign-in.md), so no top bar over it.
  const signingIn = session.kind === "signed_out" || workspaces.kind === "signed_out";
  // A sign-out reloads the page, so a session this page saw and has lost is one that ended by itself.
  const sawSession = useRef(false);
  useEffect(() => {
    if (session.kind === "signed_in") sawSession.current = true;
  }, [session.kind]);
  const gate: GateInput = {
    session,
    workspaces,
    list: state.workspaces.data,
    waiting: props.workspaceId === null && !state.unresolvable,
    current,
    personal: props.page === "account",
    detached: !!props.detachedPage,
  };
  // What the page has open, for the tab (`tab-title.ts`); the settings section is the shell's own.
  const [open, setOpen] = useState<string | null>(null);
  const title = tabTitle({
    gate: gateOf(gate),
    open: open ?? (props.page === "settings" ? (props.section?.title ?? null) : null),
    place: placeOf(state.nav, props.page),
    workspace: current && { name: current.name },
  });
  useEffect(() => {
    document.title = title;
  }, [title]);
  return (
    <TabTitleContext.Provider value={setOpen}>
      <PageShortcutsContext.Provider value={state.setPageShortcuts}>
        <WorkspaceContext.Provider value={current}>
          <NavigateContext.Provider value={state.leave}>
            <DarkThemeContext.Provider value={state.dark}>
              <div id="leadcue-reply-rep-preview" data-theme={state.dark ? "dark" : "light"}>
                <div className="lcr-app">
                  {user && <SkipLink />}
                  {user ? (
                    <ShellRail state={state} page={props.page} user={user} />
                  ) : (
                    sessionState(state.session).kind === "loading" && (
                      <>
                        <RailPending width={APP_RAIL} />
                        {state.fitted.panel > 0 && <PanelPending width={state.fitted.panel} />}
                      </>
                    )
                  )}
                  <div className="lcr-body" id="lcr-content" tabIndex={-1}>
                    {!signingIn && (
                      <TopBar
                        signedIn={user !== null}
                        dark={state.dark}
                        onHome={state.goHome}
                        menu={
                          user && <ShellPhoneMenu state={state} page={props.page} user={user} />
                        }
                      />
                    )}
                    <ShellGate
                      {...gate}
                      onRetrySession={() => void state.session.refetch()}
                      onRetryWorkspaces={() => void state.workspaces.refetch()}
                      onSignedIn={() => state.cache.invalidateQueries({ queryKey: ["session"] })}
                      ended={signingIn && sawSession.current}
                      page={() => {
                        if (props.personalPage) return props.personalPage;
                        if (props.detachedPage)
                          return (
                            user &&
                            props.detachedPage({
                              current,
                              user,
                              dark: state.dark,
                              toggleTheme: state.toggleTheme,
                              signOut: () => void state.signOut(),
                              leave: state.leave,
                            })
                          );
                        return current && user && props.children(state.value(current, user));
                      }}
                    />
                  </div>
                </div>
                {pendingLeave && (
                  <LeaveConfirm
                    onStay={() => state.setPendingLeave(null)}
                    onLeave={() => {
                      state.setPendingLeave(null);
                      pendingLeave();
                    }}
                  />
                )}
              </div>
            </DarkThemeContext.Provider>
          </NavigateContext.Provider>
        </WorkspaceContext.Provider>
      </PageShortcutsContext.Provider>
    </TabTitleContext.Provider>
  );
}
